import { Injectable, signal } from '@angular/core';
import { CxpTask } from '../models';
import { isUrlSafe } from '../../../common/security/utility/url-safety.utils';

/**
 * Stateless action surface for client-request rows in the CXP tasks list.
 *
 * Companion to `CxpDeliverableActionsService`; new client-request-only
 * actions (e.g. acknowledging a CRL, marking viewed) belong here.
 */
@Injectable({ providedIn: 'root' })
export class CxpClientRequestActionsService {

  private readonly _isOpeningExternal = signal(false);
  readonly isOpeningExternal = this._isOpeningExternal.asReadonly();

  /**
   * Hands off to a third-party CRL provider (e.g. FieldGuide) by redirecting
   * the current window. The `try` / `finally` clears the guard signal even
   * if a pre-redirect step throws — wrap any future backend call (mark
   * viewed, etc.) inside this method so the redirect remains the last step.
   *
   * The URL is re-validated with `isUrlSafe` immediately before redirecting.
   * The mapper already drops tasks with unsafe URLs, so this is defense in
   * depth against the task's state being mutated between map time and click
   * time (e.g. NGXS patch, store rehydration, dev-tools edit).
   */
  openExternal(task: CxpTask): void {
    if (this._isOpeningExternal()) return;
    if (!isUrlSafe(task.integration?.url)) return;

    this._isOpeningExternal.set(true);

    try {
      window.location.assign(task.integration!.url);
    } finally {
      this._isOpeningExternal.set(false);
    }
  }
}
